import { EmptyState } from '@/components/ui/empty-state';
import { Box, Flex, Heading, Skeleton, Stack } from '@chakra-ui/react';
import { CheckCircle } from 'iconoir-react';
import type { DashboardTodo } from '@fluenceur/types';
import { useTranslation } from 'react-i18next';
import { TodoCardMessage } from './TodoCardMessage';
import { TodoFailedRow } from './TodoFailedRow';
import { TodoRow } from './TodoRow';
import {
  TODO_LINE_HREF,
  TODO_LINE_LABEL_KEY,
  todoLineSubtitle,
  todoTotal,
  visibleTodoLines,
} from './todo-lines';

export interface DashboardTodoCardViewProps {
  /** The server's answer, absent while loading or after a failed request. */
  todo?: DashboardTodo;
  isLoading: boolean;
  /** The whole request failed: nothing to show, everything to retry. */
  isError: boolean;
  onRetry: () => void;
}

/**
 * The « À traiter » card, presentational: what waits on the brand, one link per
 * subject, from figures handed in. Fetching lives in `DashboardTodoCard`, so the
 * stories and tests render every state without a network.
 */
export const DashboardTodoCardView = ({
  todo,
  isLoading,
  isError,
  onRetry,
}: DashboardTodoCardViewProps) => {
  const { t, i18n } = useTranslation();
  const lines = todo ? visibleTodoLines(todo) : [];
  const total = todo ? todoTotal(todo) : 0;

  return (
    <Box
      as="section"
      borderWidth="1px"
      borderColor="cardBorder"
      borderRadius="16px"
      bg="panelBg"
      minW={{ base: 'auto', lg: '300px' }}
      flex="1"
      h="full"
      display="flex"
      flexDirection="column"
    >
      <Flex align="center" gap="8px" px="20px" pt="18px" pb="14px">
        <Heading as="h2" fontFamily="heading" fontSize="16px" fontWeight="600" color="ink">
          {t('dashboard.todo.title')}
        </Heading>
        {total > 0 && (
          <Flex
            data-testid="todo-total"
            align="center"
            justify="center"
            minW="22px"
            h="22px"
            px="7px"
            borderRadius="full"
            bg="darkNav"
            color="white"
            fontSize="12px"
            fontWeight="600"
          >
            {total}
          </Flex>
        )}
      </Flex>

      <Box px="8px" pb="8px" flex="1" display="flex" flexDirection="column">
        {isLoading ? (
          <Stack data-testid="todo-loading" gap="0" aria-busy="true">
            {[0, 1, 2].map((index) => (
              <Flex key={index} align="center" gap="12px" px="12px" py="11px">
                <Skeleton boxSize="36px" borderRadius="10px" />
                <Stack gap="6px" flex="1">
                  <Skeleton h="14px" w="60%" />
                  <Skeleton h="12px" w="40%" />
                </Stack>
              </Flex>
            ))}
          </Stack>
        ) : isError || !todo ? (
          <TodoCardMessage
            message={t('dashboard.todo.loadError')}
            retryLabel={t('dashboard.todo.retry')}
            onRetry={onRetry}
          />
        ) : lines.length === 0 ? (
          <EmptyState
            data-testid="todo-up-to-date"
            bare
            flex="1"
            py={8}
            borderTopWidth="1px"
            borderTopColor="border.subtle"
            rounded="none"
            icon={<CheckCircle />}
            iconTone="success"
            title={t('dashboard.todo.upToDate')}
            description={t('dashboard.todo.upToDateDescription')}
          />
        ) : (
          <Stack as="ul" gap="0" listStyleType="none" m="0" p="0">
            {lines.map((line) => (
              <Box as="li" key={line.key}>
                {line.status === 'failed' ? (
                  <TodoFailedRow
                    lineKey={line.key}
                    label={t(TODO_LINE_LABEL_KEY[line.key])}
                    message={t('dashboard.todo.loadError')}
                    retryLabel={t('dashboard.todo.retry')}
                    onRetry={onRetry}
                  />
                ) : (
                  <TodoRow
                    lineKey={line.key}
                    href={TODO_LINE_HREF[line.key]}
                    label={t(TODO_LINE_LABEL_KEY[line.key])}
                    subtitle={todoLineSubtitle(todo[line.key], line.key, t, i18n.language)}
                    count={line.count}
                  />
                )}
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
};
